/**
 * Import vocabulary — the words this wizard uses about its own source.
 *
 * The wizard serves two kinds of import, and they are not the same shape:
 *
 *   - A **file upload**. The user chose a CSV/Excel file. It has columns, it has
 *     a filename, and step 1 is choosing it.
 *   - A **source-driven import** (e.g. Lightfield). Nothing was uploaded. The
 *     data came from another company's system over an API, where records have
 *     *fields*, not columns. There is no file to name, and no upload step —
 *     the user connected the source in a different wizard entirely.
 *
 * Calling a Lightfield field a "CSV column" is false. Calling it a "column" is
 * merely vaguer — a column is a spreadsheet idea and there is no spreadsheet. So
 * each source gets the vocabulary that is true for it, rather than a
 * lowest-common-denominator word that is wrong for both.
 *
 * The wizard stays deliberately anonymous about *which* system: `CsvImport`
 * records no source identifier, so naming Lightfield here would be a guess
 * dressed up as a fact. "The system you're importing from" is the honest ceiling
 * on what today's data actually knows.
 *
 * The `ImportVocabularyProvider` component lives in its own file so this one
 * exports no components (react-refresh/only-export-components).
 */
import { createContext, useContext } from 'react';

import type { UploadedFileEntry } from '@/lib/csv-import/api';

export interface ImportVocabulary {
  /** Heading over the source side of the mapping table. */
  columnHeading: string;
  /** Label for the source-column picker in association config. */
  columnPickerLabel: string;
  /** Accessible name for that picker. */
  columnPickerAriaLabel: string;
  /** Tooltip explaining what the source-side value is. */
  columnTooltip: string;
  /** Lowercase singular noun for one source unit: "column" for a CSV upload, "field" for a source-driven import. */
  columnNoun: string;
  /** Lowercase plural noun for source units: "columns" / "fields". */
  columnNounPlural: string;
  /** Sidebar heading over the list of things being mapped. */
  groupHeading: string;
  /**
   * Accessible name for the mobile picker that stands in for the sidebar.
   * Spelled out rather than derived from `groupHeading`, which is plural —
   * lowercasing it yields "Select files to view".
   */
  groupPickerAriaLabel: string;
  /** Mapping-step title. */
  mappingTitle: string;
  /** Mapping-step subtitle. */
  mappingSubtitle: string;
  /**
   * Whether this import has an upload step at all. A source-driven import has no
   * file to choose, so rendering the step leaves a file-drop screen reachable
   * via Back for an import that can never have one.
   */
  hasUploadStep: boolean;
  /** Tooltip for the "Map to property" column header, shown to schema admins. */
  mapToPropertyTooltipAdmin: string;
  /** Tooltip for the "Map to property" column header, shown to non-admins. */
  mapToPropertyTooltipNonAdmin: string;
  /** Tooltip on the disabled "Add new property" CTA shown to non-admins (column vs field wording). */
  addPropertyDisabledTooltip: string;
  /** Tooltip for the "Record matching" column header. */
  recordMatchingTooltip: string;
}

const FILE_VOCABULARY: ImportVocabulary = {
  columnHeading: 'CSV column',
  columnPickerLabel: 'CSV column',
  columnPickerAriaLabel: 'Source CSV column',
  columnTooltip: 'The column header from your uploaded file',
  columnNoun: 'column',
  columnNounPlural: 'columns',
  groupHeading: 'Files',
  groupPickerAriaLabel: 'Select file to view',
  mappingTitle: 'Map your columns',
  mappingSubtitle:
    "Match each CSV column to a CRM property using the dropdowns. Drag rows to reorder how they appear in the import. Columns you don't map aren't imported.",
  hasUploadStep: true,
  mapToPropertyTooltipAdmin:
    "The CRM property this column imports into. Columns you don't map aren't imported. To create a new custom property, choose \"Add new property\" in the column's dropdown.",
  mapToPropertyTooltipNonAdmin:
    "The CRM property this column imports into. Columns you don't map aren't imported.",
  addPropertyDisabledTooltip:
    'Only admins can create new properties. Map this column to an existing property, or ask an admin.',
  recordMatchingTooltip:
    "Controls whether rows update existing records or always create new ones. 'Update on match' finds a record by this column's value – if one matches, it's updated, if none match, a new record is created, and if more than one matches, the row is skipped.",
};

const SOURCE_VOCABULARY: ImportVocabulary = {
  columnHeading: 'Field',
  columnPickerLabel: 'Field',
  columnPickerAriaLabel: 'Source field',
  columnTooltip: 'The field name in the system you’re importing from',
  columnNoun: 'field',
  columnNounPlural: 'fields',
  groupHeading: 'Record types',
  groupPickerAriaLabel: 'Select record type to view',
  mappingTitle: 'Map your fields',
  mappingSubtitle:
    "Match each field to a CRM property using the dropdowns. Drag rows to reorder how they appear in the import. Fields you don't map aren't imported.",
  hasUploadStep: false,
  mapToPropertyTooltipAdmin:
    "The CRM property this field imports into. Fields you don't map aren't imported. To create a new custom property, choose \"Add new property\" in the field's dropdown.",
  mapToPropertyTooltipNonAdmin:
    "The CRM property this field imports into. Fields you don't map aren't imported.",
  addPropertyDisabledTooltip:
    'Only admins can create new properties. Map this field to an existing property, or ask an admin.',
  recordMatchingTooltip:
    "Controls whether rows update existing records or always create new ones. 'Update on match' finds a record by this field's value – if one matches, it's updated, if none match, a new record is created, and if more than one matches, the row is skipped.",
};

/**
 * A source-driven import fabricates one virtual `uploaded_files` entry per
 * entity type, each stamped with `source_import_anchor_file_id` pointing at a
 * byte-less anchor (`csv_imports/services/source_import.py`). The backend sets
 * that stamp for exactly this class of import and nothing else, which makes it a
 * truer signal than "the filename happens to be empty".
 */
export function isSourceDrivenImport(files: UploadedFileEntry[]): boolean {
  return files.some(file => file.source_import_anchor_file_id !== undefined);
}

export function vocabularyForFiles(
  files: UploadedFileEntry[]
): ImportVocabulary {
  return isSourceDrivenImport(files) ? SOURCE_VOCABULARY : FILE_VOCABULARY;
}

/**
 * Defaults to the file vocabulary: that is the historical wording and the
 * overwhelmingly common case, so a consumer rendered outside a provider reads
 * exactly as it did before this seam existed.
 */
export const ImportVocabularyContext =
  createContext<ImportVocabulary>(FILE_VOCABULARY);

/**
 * Read the wizard's vocabulary. Context rather than props: the words are needed
 * three levels down (mapping step → file section → association config), and
 * threading them through every intermediate component is exactly the prop
 * drilling FRONTEND_PATTERNS warns against.
 */
export function useImportVocabulary(): ImportVocabulary {
  return useContext(ImportVocabularyContext);
}
